import React, { useState } from 'react';
import {
  BookOpen,
  CheckCircle2,
  QrCode,
  ChevronLeft,
  ChevronRight,
  ShoppingCart,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
} from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { LazyBookCover } from '../../../../shared/components/LazyBookCover.js';
import { QrCodeViewer } from '../../../../shared/components/QrCodeViewer.js';
import { BarcodeViewer } from '../../../../shared/components/BarcodeViewer.js';
import { useAuthStore } from '../../../auth/store/useAuthStore.js';
import { isOwnerOrAdmin } from '../../../books/components/book-list/bookListUtils.js';

interface LandingSearchResultsProps {
  searchResults: Book[];
  searchQuery: string;
  searchPage: number;
  itemsPerPage: number;
  onSelectBook: (book: Book) => void;
  onCloseResults: () => void;
  onPageChange: (page: number) => void;
}

export const LandingSearchResults: React.FC<LandingSearchResultsProps> = ({
  searchResults,
  searchQuery,
  searchPage,
  itemsPerPage,
  onSelectBook,
  onCloseResults,
  onPageChange,
}) => {
  const { user } = useAuthStore();
  const [expandedCodeBookId, setExpandedCodeBookId] = useState<string | null>(null);
  const [activeCodeTab, setActiveCodeTab] = useState<'qr' | 'barcode'>('qr');

  const totalPages = Math.ceil(searchResults.length / itemsPerPage);

  const handlePageSelect = (page: number) => {
    onPageChange(page);
    document.getElementById('search-results-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleOpticalCode = (bookKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedCodeBookId((prev) => (prev === bookKey ? null : bookKey));
  };

  return (
    <div
      id="search-results-container"
      className="mt-8 text-left max-w-4xl 2xl:max-w-5xl mx-auto bg-white rounded-2xl 2xl:rounded-3xl border border-slate-200 shadow-lg overflow-hidden animate-in fade-in duration-150"
    >
      <div className="px-4 sm:px-6 py-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-indigo-600"></span>
          <span className="text-xs sm:text-sm 2xl:text-base font-bold text-slate-800">
            Hasil Pencarian ({searchResults.length} buku ditemukan)
            {totalPages > 1 && (
              <span className="text-xs sm:text-sm font-medium text-slate-500 ml-1.5">
                • Halaman {searchPage} dari {totalPages}
              </span>
            )}
          </span>
        </div>
        <button
          type="button"
          onClick={onCloseResults}
          className="px-3 py-1.5 text-xs sm:text-sm text-slate-500 hover:text-slate-800 hover:bg-slate-200/60 rounded-lg transition font-medium cursor-pointer"
        >
          Tutup Hasil
        </button>
      </div>

      {searchResults.length === 0 ? (
        <div className="p-8 sm:p-12 text-center">
          <BookOpen className="w-10 h-10 2xl:w-12 2xl:h-12 text-slate-300 mx-auto mb-2.5" />
          <h4 className="text-sm sm:text-base 2xl:text-lg font-bold text-slate-700 mb-1">Buku Tidak Ditemukan</h4>
          <p className="text-xs sm:text-sm 2xl:text-base text-slate-500 max-w-md 2xl:max-w-lg mx-auto">
            Tidak ada naskah buku yang sesuai dengan kata kunci &quot;{searchQuery}&quot;. Periksa kembali penulisan judul, nomor UIBN, atau nama penulis.
          </p>
        </div>
      ) : (
        <>
          <div className="divide-y divide-slate-100">
            {searchResults
              .slice((searchPage - 1) * itemsPerPage, searchPage * itemsPerPage)
              .map((b, idx) => {
                const bookKey = b.id || b.uibn || `book-${idx}`;
                const isCodeExpanded = expandedCodeBookId === bookKey;
                const canDownload = isOwnerOrAdmin(b, user);

                return (
                  <div
                    key={`landing-res-${bookKey}-${idx}`}
                    className="hover:bg-indigo-50/30 transition flex flex-col"
                  >
                    <div
                      onClick={() => onSelectBook(b)}
                      className="p-4 sm:p-6 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4 group"
                    >
                      <div className="flex items-start gap-3.5 sm:gap-5 flex-1 min-w-0">
                        {/* Cover Thumbnail */}
                        <LazyBookCover
                          coverUrl={b.coverUrl}
                          title={b.title}
                          className="w-14 h-20 sm:w-18 sm:h-26 2xl:w-20 2xl:h-28 rounded-lg shrink-0 shadow-xs border border-slate-200/70"
                          targetWidth={200}
                          quality={75}
                          hoverEffect
                        />

                        {/* Content Details: Full unclipped title & rich metadata */}
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                            <span className="font-mono text-xs sm:text-sm font-bold text-indigo-700 bg-indigo-50 px-2.5 py-0.5 rounded-md border border-indigo-100">
                              {b.uibn}
                            </span>
                            <span className="text-[10px] sm:text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md">
                              {b.category}
                            </span>
                            {b.edition && (
                              <span className="text-[10px] sm:text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-md">
                                {b.edition}
                              </span>
                            )}
                            {b.status === 'disetujui' && (
                              <span className="text-[10px] sm:text-xs text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Sah
                              </span>
                            )}
                          </div>

                          {/* Complete, fully readable book title without truncation */}
                          <h4 className="text-sm sm:text-base 2xl:text-lg font-bold text-slate-900 leading-snug break-words group-hover:text-indigo-600 transition-colors">
                            {b.title}
                          </h4>

                          {b.originalTitle && (
                            <p className="text-xs sm:text-sm text-slate-400 italic">
                              Judul Asli: {b.originalTitle}
                            </p>
                          )}

                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs sm:text-sm text-slate-500 pt-0.5">
                            <span>Penulis: <strong className="font-medium text-slate-700">{b.authorName}</strong></span>
                            <span className="text-slate-300">•</span>
                            <span>Penerbit: <strong className="font-medium text-slate-700">{b.publisherName}</strong></span>
                            <span className="text-slate-300">•</span>
                            <span>Tahun: <strong className="font-medium text-slate-700">{b.publicationYear}</strong></span>
                          </div>
                        </div>
                      </div>

                      {/* Dedicated Action Buttons */}
                      <div className="shrink-0 flex items-center justify-end sm:self-center gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100 flex-wrap">
                        {b.purchaseUrl && (
                          <a
                            href={b.purchaseUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="px-3 sm:px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-2xs cursor-pointer"
                            title="Beli Buku Resmi di Marketplace / Toko Penerbit"
                          >
                            <ShoppingCart className="w-3.5 h-3.5" />
                            <span>Beli Buku</span>
                            <ExternalLink className="w-3 h-3 opacity-80" />
                          </a>
                        )}

                        {/* Toggle Barcode/QR Code View Button */}
                        <button
                          type="button"
                          onClick={(e) => toggleOpticalCode(bookKey, e)}
                          className={`px-3 py-2 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 border cursor-pointer shadow-2xs ${
                            isCodeExpanded
                              ? 'bg-slate-800 text-white border-slate-800'
                              : 'bg-white hover:bg-slate-100 text-slate-700 border-slate-200'
                          }`}
                          title="Tampilkan Kode QR dan Barcode Buku"
                        >
                          <QrCode className="w-3.5 h-3.5 text-indigo-500" />
                          <span>Kode Optik</span>
                          {isCodeExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5 opacity-60" />}
                        </button>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectBook(b);
                          }}
                          className="px-3.5 py-2 bg-indigo-50 hover:bg-indigo-600 text-indigo-700 hover:text-white rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 border border-indigo-200 hover:border-transparent shadow-2xs cursor-pointer"
                          title="Buka rincian lengkap buku"
                        >
                          <span>Info Lengkap</span>
                        </button>
                      </div>
                    </div>

                    {/* Inline Expandable Optical Code (QR / Barcode) Panel */}
                    {isCodeExpanded && (
                      <div className="mx-4 sm:mx-6 mb-4 p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3 animate-in fade-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between flex-wrap gap-2 pb-2 border-b border-slate-200/80">
                          <div className="flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4 text-indigo-600" />
                            <span className="text-xs font-bold text-slate-800 uppercase tracking-wide">
                              Kode Optik UIBN: <span className="font-mono text-indigo-700">{b.uibn}</span>
                            </span>
                          </div>

                          <div className="flex bg-slate-200/80 p-0.5 rounded-lg text-xs font-medium">
                            <button
                              type="button"
                              onClick={() => setActiveCodeTab('qr')}
                              className={`px-2.5 py-1 rounded-md transition cursor-pointer ${
                                activeCodeTab === 'qr' ? 'bg-white text-slate-900 shadow-xs font-bold' : 'text-slate-600 hover:text-slate-900'
                              }`}
                            >
                              QR Code 2D
                            </button>
                            <button
                              type="button"
                              onClick={() => setActiveCodeTab('barcode')}
                              className={`px-2.5 py-1 rounded-md transition cursor-pointer ${
                                activeCodeTab === 'barcode' ? 'bg-white text-slate-900 shadow-xs font-bold' : 'text-slate-600 hover:text-slate-900'
                              }`}
                            >
                              Barcode 1D
                            </button>
                          </div>
                        </div>

                        <div className="flex justify-center">
                          {activeCodeTab === 'qr' ? (
                            <QrCodeViewer
                              uibn={b.uibn}
                              bookTitle={b.title}
                              showDownload={canDownload}
                              size={140}
                              className="max-w-md w-full"
                            />
                          ) : (
                            <BarcodeViewer
                              uibn={b.uibn}
                              bookTitle={b.title}
                              showDownload={canDownload}
                              onSwitchToQr={() => setActiveCodeTab('qr')}
                              className="max-w-md w-full"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          {/* Pagination Controls Banner */}
          {searchResults.length > itemsPerPage && (
            <div className="px-4 sm:px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm">
              <div className="text-slate-500 font-medium text-center sm:text-left">
                Menampilkan <span className="font-bold text-slate-800">{(searchPage - 1) * itemsPerPage + 1}</span> -{' '}
                <span className="font-bold text-slate-800">{Math.min(searchPage * itemsPerPage, searchResults.length)}</span>{' '}
                dari <span className="font-bold text-slate-800">{searchResults.length}</span> hasil
              </div>

              <div className="flex items-center gap-1.5 flex-wrap justify-center">
                <button
                  type="button"
                  disabled={searchPage === 1}
                  onClick={() => handlePageSelect(Math.max(searchPage - 1, 1))}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition font-medium flex items-center gap-1 cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Sebelumnya</span>
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    type="button"
                    key={`landing-search-page-${pageNum}`}
                    onClick={() => handlePageSelect(pageNum)}
                    className={`w-8 h-8 rounded-lg text-xs font-bold transition flex items-center justify-center cursor-pointer ${
                      searchPage === pageNum
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

                <button
                  type="button"
                  disabled={searchPage === totalPages}
                  onClick={() => handlePageSelect(Math.min(searchPage + 1, totalPages))}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition font-medium flex items-center gap-1 cursor-pointer"
                >
                  <span>Selanjutnya</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
